import {
  CheckIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { Checkbox, Drawer, Input, Tabs } from "antd"
import { Button as CustomButton } from "components"
import { debounce } from "lodash"
import { useEffect, useMemo, useState } from "react"
import { TIMESHEET_DRAWER_WIDTH } from "../../constants"
import type {
  DuplicateAction,
  UploadTimeSheetFailedEntity,
} from "../../types"
import ConflictTable, {
  buildDuplicateKey,
  type DuplicateRow,
} from "./ConflictTable"

interface Props {
  open: boolean
  failed: UploadTimeSheetFailedEntity | undefined
  resolutions: Record<string, DuplicateAction>
  onSetResolution: (keys: string[], action: DuplicateAction) => void
  onClose: () => void
}

type DuplicateTab = "unresolved" | "resolved"

const DuplicateConflictsDrawer = ({
  open,
  failed,
  resolutions,
  onSetResolution,
  onClose,
}: Props) => {
  const [search, setSearch] = useState("")
  const [searchedWord, setSearchedWord] = useState("")
  const debouncedSearch = useMemo(
    () => debounce((value: string) => setSearchedWord(value), 250),
    []
  )
  useEffect(() => {
    debouncedSearch(search)
    return () => debouncedSearch.cancel()
  }, [search, debouncedSearch])
  const [tab, setTab] = useState<DuplicateTab>("unresolved")
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [collapsedDepts, setCollapsedDepts] = useState<
    Record<string, boolean>
  >({})

  const allRows: DuplicateRow[] = useMemo(() => {
    const groups = failed?.employeesAffected ?? []
    return groups.flatMap((g) =>
      (g.employees ?? []).map((e) => ({
        key: buildDuplicateKey(
          e.employeeId ?? e.record?.employeeId,
          e.affectedRecord?.id
        ),
        employee: e,
        departmentId: String(g.department?.id ?? 0),
        departmentName: g.department?.name || "Unknown Department",
      }))
    )
  }, [failed])

  const filteredRows = useMemo(() => {
    const q = searchedWord.trim().toLowerCase()
    return allRows.filter((r) => {
      const e = r.employee
      const matchesSearch =
        !q ||
        `${e.firstName ?? ""} ${e.lastName ?? ""}`
          .toLowerCase()
          .includes(q) ||
        String(e.employeeId ?? e.record?.employeeId ?? "").includes(q) ||
        (e.email ?? "").toLowerCase().includes(q)
      if (!matchesSearch) return false
      const resolved = !!resolutions[r.key]
      return tab === "resolved" ? resolved : !resolved
    })
  }, [allRows, searchedWord, resolutions, tab])

  const groupedDepts = useMemo(() => {
    const grouped = new Map<string, { name: string; rows: DuplicateRow[] }>()
    for (const r of filteredRows) {
      if (!grouped.has(r.departmentId)) {
        grouped.set(r.departmentId, { name: r.departmentName, rows: [] })
      }
      grouped.get(r.departmentId)!.rows.push(r)
    }
    return Array.from(grouped.entries())
  }, [filteredRows])

  const filteredKeys = useMemo(
    () => filteredRows.map((r) => r.key),
    [filteredRows]
  )
  const allFilteredSelected =
    filteredKeys.length > 0 &&
    filteredKeys.every((k) => selectedKeys.includes(k))

  const setResolutionAndClearSelection = (
    keys: string[],
    action: DuplicateAction
  ) => {
    onSetResolution(keys, action)
    setSelectedKeys([])
  }

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedKeys((prev) => prev.filter((k) => !filteredKeys.includes(k)))
    } else {
      setSelectedKeys((prev) =>
        Array.from(new Set([...prev, ...filteredKeys]))
      )
    }
  }

  const toggleKeys = (keys: string[]) => {
    const allSelected = keys.every((k) => selectedKeys.includes(k))
    if (allSelected) {
      setSelectedKeys((prev) => prev.filter((k) => !keys.includes(k)))
    } else {
      setSelectedKeys((prev) => Array.from(new Set([...prev, ...keys])))
    }
  }

  const closeAndReset = () => {
    setSearch("")
    setSearchedWord("")
    setSelectedKeys([])
    setCollapsedDepts({})
    setTab("unresolved")
    onClose()
  }

  return (
    <Drawer
      open={open}
      onClose={closeAndReset}
      title={null}
      closable={false}
      width={TIMESHEET_DRAWER_WIDTH}
      footer={
        <div className="flex justify-end">
          <CustomButton
            color="primary"
            className="font-semibold"
            onClick={closeAndReset}
          >
            Done
          </CustomButton>
        </div>
      }
      styles={{
        body: { padding: 0 },
        footer: { padding: "16px", borderTop: "1px solid #E2E8F0" },
      }}
    >
      <div className="h-full flex flex-col">
        <div className="flex items-start justify-between px-4 pt-4 pb-4">
          <div className="flex flex-col gap-1">
            <h3 className="font-bold text-lg text-[#0F172B] m-0">
              Employees with duplicate records
            </h3>
            <p className="text-sm text-[#64748B] m-0">
              Select employees to assign to this shift plan.
            </p>
          </div>
          <button
            className="p-1 hover:bg-[#F1F5F9] rounded text-[#64748B] text-xl leading-none"
            onClick={closeAndReset}
            type="button"
          >
            ✕
          </button>
        </div>

        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as DuplicateTab)}
          items={[
            { key: "unresolved", label: "Unresolved conflicts" },
            { key: "resolved", label: "Resolved conflicts" },
          ]}
          className="px-4 [&_.ant-tabs-nav-list]:!w-full [&_.ant-tabs-tab]:!flex-1 [&_.ant-tabs-tab]:!justify-center"
        />

        <div className="px-4 py-2 border-b border-[#E2E8F0]">
          <Input
            placeholder="Search for a staff"
            prefix={
              <MagnifyingGlassIcon className="w-4 h-4 text-[#94A3B8]" />
            }
            className="!border-0 !shadow-none !outline-none"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="px-4 py-3 flex items-center justify-between border-b border-[#E2E8F0]">
          <Checkbox
            checked={allFilteredSelected}
            indeterminate={
              !allFilteredSelected &&
              filteredKeys.some((k) => selectedKeys.includes(k))
            }
            onChange={toggleSelectAll}
          >
            <span className="text-sm">Select all</span>
          </Checkbox>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={selectedKeys.length === 0}
              onClick={() =>
                setResolutionAndClearSelection(selectedKeys, "overwrite")
              }
              className="border-2 border-[#004AF5] text-[#004AF5] font-semibold rounded-md px-3 py-2 text-sm flex items-center gap-1 disabled:opacity-50"
            >
              <CheckIcon className="w-4 h-4" />
              Bulk overwrite
            </button>
            <button
              type="button"
              disabled={selectedKeys.length === 0}
              onClick={() =>
                setResolutionAndClearSelection(selectedKeys, "reject")
              }
              className="border-2 border-[#EF4444] text-[#EF4444] font-semibold rounded-md px-3 py-2 text-sm flex items-center gap-1 disabled:opacity-50"
            >
              <XMarkIcon className="w-4 h-4" />
              Bulk reject
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4">
          <ConflictTable
            groupedDepts={groupedDepts}
            collapsedDepts={collapsedDepts}
            selectedKeys={selectedKeys}
            resolutions={resolutions}
            onToggleCollapse={(deptId) =>
              setCollapsedDepts((prev) => ({
                ...prev,
                [deptId]: !prev[deptId],
              }))
            }
            onToggleDept={toggleKeys}
            onToggleEmployee={toggleKeys}
            onSetResolution={setResolutionAndClearSelection}
          />
        </div>
      </div>
    </Drawer>
  )
}

export default DuplicateConflictsDrawer
